import React from 'react';

import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { TranslatedRichText } from '@/components/TranslatedRichText';
import { KycSection } from '@/pages/settings/verify/kyc-sections/KycSection';
import { selectKycDocumentPhotoId } from '@/store/selectors/kycSelectors';
import { KycType, VerificationStatus } from '@lucky7ventures/bff-types';
import KycDescription from '@/pages/settings/verify/kyc-sections/KycDescription';
import KycDocument, { DropzoneMeta } from '@/pages/settings/verify/kyc-sections/KycDocument';
import { useModal } from '@/features/modal/useModal';
import { MODALS } from '@/features/modal/types';
import { selectIsFinOrSv, selectIsThailand } from '@/store/selectors/commonSelectors';

export function KycPhotoID(): JSX.Element | null {
  const intl = useIntl();
  const photoId = useSelector(selectKycDocumentPhotoId);
  const { openModal } = useModal();
  const openIdScan = () => openModal(MODALS.CHECKIN_ID_SCAN, { noBackdropClick: true });
  const isFinOrSv = useSelector(selectIsFinOrSv);
  const isThailand = useSelector(selectIsThailand);
  if (!photoId) {
    return null;
  }

  // Checkin ID scan for all markets except sweden, finland and thailand
  if (!isFinOrSv && !isThailand) {
    return (
      <KycSection
        status={photoId.status}
        title={intl.formatMessage({ id: 'settings.verify.photoId.header' })}
      >
        {/* we want to hide the text for approved and pending status */}
        {[VerificationStatus.Approved, VerificationStatus.Processing].indexOf(photoId.status) ===
          -1 && (
          <TranslatedRichText
            data-cy="gigb-id-scan-text"
            id="settings.verify.photoId.gbg.idScan.text"
            tagName="div"
          />
        )}
        {[VerificationStatus.Requested, VerificationStatus.UserRequested].indexOf(photoId.status) >
          -1 && (
          <button
            type="button"
            className="mt-8 mb-4 block h-14 cursor-pointer rounded-full bg-blue-light px-12 font-bold text-white shadow-md transition hover:bg-cyan-600 hover:shadow-lg disabled:opacity-60"
            onClick={openIdScan}
          >
            {intl.formatMessage({ id: 'misc.verifyNow' })}
          </button>
        )}
      </KycSection>
    );
  }

  const dropzonesMeta: DropzoneMeta[] = [
    {
      id: KycType.Id.toString(),
      minFiles: 1, // We need 1 file for PhotoID
      maxFiles: 1, // We need 1 file for PhotoID
      errorMessageTranslationKey: 'settings.verify.upload.error.text',
    },
  ];

  return (
    <KycDocument
      type={KycType.Id}
      status={photoId.status}
      acceptedFileTypes={['image/png', 'image/jpeg', 'application/pdf']}
      title={intl.formatMessage({ id: 'settings.verify.photoId.header' })}
      description={<KycDescription kycType={KycType.Id} />}
      dropzonesMeta={dropzonesMeta}
      validationErrorMessage={intl.formatMessage({
        id: 'settings.verify.upload.error.text',
      })}
      key={KycType.Id}
    />
  );
}
